import { Flex } from 'components/primitives/flex';
import Head from 'next/head';
import { processQueryOut } from 'stacks/search/queryHelpers';
import PaginationComponent from 'components/Pagination';
import { useBreakpoint } from 'components/BreakpointProvider';
import { formatNumber } from 'helpers/currency';
import DefaultLayout from 'components/layouts/DefaultLayout';
import { useRouter } from 'next/router';
import Link from 'next/link';
import useModal from 'components/remodal/v2/useModal';
import BoatDeleteModal from 'components/modals/BoatDeleteModal';
import { useState } from 'react';
import Button from 'components/ui/Button/Button';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import styled from 'styled-components';
import CameraIcon from 'components/icons/CameraIcon';
import { useUserDetails } from 'auth/mutations/userHooks';
import boatsetterPaths from 'utils/boatsetterPaths';
import { getItunesAppUniversalLinkMetaTag, OWNER_APP_ID } from 'helpers/templateHelpers';
import { deniedStates, pendingStates } from 'components/manageBoat/utils';
import { useGetMyBoatsQuery } from 'components/manage_boats/hooks';
import { getAsNumber } from 'helpers';
import BoatStatusBadge from 'components/boats/BoatStatusBadge';
import { mediaMdMax, mediaMSmMax, mediaMMdMin } from 'helpers/breakpoints';
import NextImageWithShimmer, { getPathnameFromUrl } from 'components/image/NextImageWithShimmer';
import { Box } from 'components/primitives/box';

const severityColorMap = {
  warning: '#F4C064',
  info: '#17233C',
  critical: '#BC350A',
};

const ViewsContainer = ({ pageviewCount }) => (
  <div className="viewsContainer">
    <img src="/images/manage_boats/views.svg" alt="" />
    <span>
      {formatNumber(pageviewCount || 0)}
      <span>view{pageviewCount !== 1 ? 's' : ''}</span>
    </span>
  </div>
);

const MyBoatsPage = () => {
  const { query, asPath } = useRouter();

  const { data: { data: myBoats, headers } = {}, isLoading } = useGetMyBoatsQuery(getAsNumber(query?.page) || 1);
  const {
    userDetails: { is_boat_manager },
  } = useUserDetails();

  const breakpoints = useBreakpoint();
  const smallScreen: boolean = breakpoints.xs || breakpoints.sm;

  const [boatToDelete, setBoatToDelete] = useState(null);

  const { modal: boatDeleteModal, openModal: openBoatDeleteModal } = useModal(<BoatDeleteModal boat={boatToDelete} />, {
    trackingName: 'boatDeleteModal',
    size: 'medium',
  });

  const openBoat = (boat) => {
    if (boat.state === 'incomplete') {
      window.location.href = `/l/boats/${boat.id}/edit`;
    } else {
      window.location.href = `/boats/${boat.id}/edit`;
    }
  };

  const deleteBoat = (boat) => {
    setBoatToDelete(boat);
    openBoatDeleteModal();
  };

  const getLabelDescription = ({ state, activated }) => {
    if (!activated && state === 'approved') return 'Listing not visible in search and not bookable';
    if (pendingStates.includes(state)) return 'Listing under review';
    if (state === 'incomplete') return 'Listing incomplete';
    if (deniedStates.includes(state)) {
      return (
        <div>
          Listed denied. <br /> Please contact support: (855) 908-3869
        </div>
      );
    }
    if (state === 'approved') return 'Listing visible in search and bookable';
  };

  const router = useRouter();

  const needsBuoyUnderwritingAction = (boat) =>
    boat.actions_required.filter((ac) => ac.action_type === 'buoy_underwriting');

  const needsPricingAction = (boat) => boat.actions_required.filter((ac) => ac.action_type === 'price_missing');

  return (
    <DefaultLayout ownerNav isProtected loading={isLoading} path="/manage_boats" white>
      <Head>{getItunesAppUniversalLinkMetaTag(asPath, OWNER_APP_ID)}</Head>
      <Wrapper style={{ paddingTop: 60 }}>
        <div
          className="arrow"
          onClick={() => {
            window.location.href = boatsetterPaths.ownerDashboard;
          }}
        >
          <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M-6.88673e-07 7.87006L7.66 15.5301C7.95 15.8201 8.43 15.8201 8.72 15.5301C9.01 15.2401 9.01 14.7601 8.72 14.4701L2.12 7.87006L8.72 1.27006C9.01 0.980059 9.01 0.500058 8.72 0.210058C8.43 -0.0799417 7.95 -0.0799417 7.66 0.210058L-6.88673e-07 7.87006Z"
              fill="#0E7873"
            />
          </svg>
          <span>Owner dashboard</span>
        </div>

        <div className="header">
          <h1>My boats</h1>
          <Button
            outline
            variant="owner"
            onClick={() => router.push(boatsetterPaths.listBoat)}
            text="list a new boat"
          />
        </div>

        <div className="boatsList">
          {myBoats?.map((boat) => (
            <div key={boat.id} className="boat">
              <div className="imageContainer">
                {['boat-power.png', 'boat-sail.png'].includes(boat.best_photo_url) || !boat.best_photo_url ? (
                  <div className="incompleteBoat">
                    <CameraIcon />
                  </div>
                ) : (
                  <Box
                    style={{
                      width: 210,
                      height: 150,
                    }}
                  >
                    <NextImageWithShimmer
                      alt=""
                      src={getPathnameFromUrl(boat.best_photo_url)}
                      fill
                      sizes="210px"
                      style={{ objectFit: 'cover' }}
                    />
                  </Box>
                )}
                {boat.state === 'approved' && <ViewsContainer pageviewCount={boat.pageview_count || 0} />}
              </div>
              <div className="data">
                <div className={`heading ${boat.state !== 'approved' ? 'shortPadding' : ''}`}>
                  <div className="container" style={{ position: 'relative' }}>
                    <Flex>
                      <Tooltip
                        distance={15}
                        triggerWith={<BoatStatusBadge state={boat.state} activated={boat.activated} />}
                      >
                        <div>{getLabelDescription(boat as { state: string; activated: boolean })}</div>
                      </Tooltip>
                      {needsPricingAction(boat).length > 0 && needsBuoyUnderwritingAction(boat).length === 0 && (
                        <Tooltip
                          distance={15}
                          style={{ marginLeft: smallScreen ? '10px' : 'auto' }}
                          triggerWith={
                            <a href={`${boatsetterPaths.editListing}/${boat.id}/manage/durations_and_pricing`}>
                              <svg
                                width={20}
                                height={20}
                                viewBox="0 0 28 26"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M27.327 19.9455L17.5136 2.33866C16.8066 1.0868 15.4899 0.333008 14.0206 0.333008C12.5375 0.333008 11.2346 1.0868 10.5277 2.33866L0.672599 19.932C-0.00658335 21.1435 0.0211384 22.5838 0.741903 23.7683C1.46267 24.9529 2.75173 25.6663 4.1794 25.6663H23.8341C25.2479 25.6663 26.537 24.9529 27.2577 23.7683C27.9785 22.5972 28.0062 21.1704 27.327 19.9455ZM25.4697 22.7454C25.1232 23.3107 24.5133 23.6472 23.8341 23.6472H4.16554C3.48636 23.6472 2.87648 23.3107 2.51609 22.7454C2.15571 22.18 2.15571 21.4934 2.47451 20.9146L12.3296 3.32128C12.6761 2.71554 13.2998 2.36561 14.0068 2.36561C14.7137 2.36561 15.3374 2.729 15.6839 3.32128L25.4974 20.9281C25.8301 21.4935 25.8162 22.18 25.4697 22.7454ZM12.9533 13.1746V8.23452C12.9533 7.68263 13.4246 7.22496 13.9929 7.22496C14.5612 7.22496 15.0325 7.68263 15.0325 8.23452V13.1746C15.0325 13.7265 14.5612 14.1842 13.9929 14.1842C13.4246 14.1842 12.9533 13.7265 12.9533 13.1746ZM15.9611 18.9359C15.9611 19.9859 15.074 20.8473 13.9929 20.8473C12.9117 20.8473 12.0246 19.9859 12.0246 18.9359C12.0246 17.886 12.9117 17.0245 13.9929 17.0245C15.074 17.0245 15.9611 17.8725 15.9611 18.9359Z"
                                  fill={severityColorMap.critical}
                                />
                              </svg>
                            </a>
                          }
                        >
                          This listing requires prices before it can go live
                        </Tooltip>
                      )}
                      {needsBuoyUnderwritingAction(boat).length > 0 && (
                        <Tooltip
                          distance={15}
                          style={{ marginLeft: smallScreen ? '10px' : 'auto' }}
                          triggerWith={
                            <svg
                              width={20}
                              height={20}
                              viewBox="0 0 28 26"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M27.327 19.9455L17.5136 2.33866C16.8066 1.0868 15.4899 0.333008 14.0206 0.333008C12.5375 0.333008 11.2346 1.0868 10.5277 2.33866L0.672599 19.932C-0.00658335 21.1435 0.0211384 22.5838 0.741903 23.7683C1.46267 24.9529 2.75173 25.6663 4.1794 25.6663H23.8341C25.2479 25.6663 26.537 24.9529 27.2577 23.7683C27.9785 22.5972 28.0062 21.1704 27.327 19.9455ZM25.4697 22.7454C25.1232 23.3107 24.5133 23.6472 23.8341 23.6472H4.16554C3.48636 23.6472 2.87648 23.3107 2.51609 22.7454C2.15571 22.18 2.15571 21.4934 2.47451 20.9146L12.3296 3.32128C12.6761 2.71554 13.2998 2.36561 14.0068 2.36561C14.7137 2.36561 15.3374 2.729 15.6839 3.32128L25.4974 20.9281C25.8301 21.4935 25.8162 22.18 25.4697 22.7454ZM12.9533 13.1746V8.23452C12.9533 7.68263 13.4246 7.22496 13.9929 7.22496C14.5612 7.22496 15.0325 7.68263 15.0325 8.23452V13.1746C15.0325 13.7265 14.5612 14.1842 13.9929 14.1842C13.4246 14.1842 12.9533 13.7265 12.9533 13.1746ZM15.9611 18.9359C15.9611 19.9859 15.074 20.8473 13.9929 20.8473C12.9117 20.8473 12.0246 19.9859 12.0246 18.9359C12.0246 17.886 12.9117 17.0245 13.9929 17.0245C15.074 17.0245 15.9611 17.8725 15.9611 18.9359Z"
                                fill={severityColorMap.critical}
                              />
                            </svg>
                          }
                        >
                          {is_boat_manager && boat.managed_by_boat_manager
                            ? 'This listing has a new insurance requirement.'
                            : 'Update this listing to meet the latest insurance requirements'}
                        </Tooltip>
                      )}
                    </Flex>

                    <div className="title">
                      {boat.state === 'incomplete' ? (
                        `${boat.make} ${boat.model}`
                      ) : (
                        <Link href={`/boats/${boat.id}`} legacyBehavior>
                          {boat.make || boat.model
                            ? `${boat.make && `${boat.make} `}${boat.model}`
                            : 'No make and model set'}
                        </Link>
                      )}
                    </div>
                  </div>
                  {['boat-power.png', 'boat-sail.png'].includes(boat.best_photo_url) || !boat.best_photo_url ? (
                    <div className="incompleteBoat">
                      <CameraIcon />
                    </div>
                  ) : (
                    <Box
                      style={{
                        borderRadius: '5px',
                        width: 60,
                        position: 'relative',
                        aspectRatio: '1/1',
                        overflow: 'hidden',
                      }}
                    >
                      <NextImageWithShimmer
                        alt=""
                        src={getPathnameFromUrl(boat.best_photo_url)}
                        fill
                        sizes="60px"
                        style={{ objectFit: 'cover' }}
                      />
                    </Box>
                  )}
                </div>
                <div className={`bottom ${boat.state === 'approved' && 'border-top'}`}>
                  <div className="left">
                    {boat.state === 'approved' && (
                      <div className="icons">
                        <div className="instantBook icon">
                          <img
                            src={
                              boat.is_instant_book
                                ? '/images/manage_boats/instant.svg'
                                : '/images/manage_boats/instant_off.svg'
                            }
                            alt=""
                          />
                          <span>
                            Instant book <b>{boat.is_instant_book ? 'ON' : 'OFF'}</b>
                          </span>
                        </div>
                        {boat.managed_by_boat_manager && (
                          <div className="managed icon">
                            <img src="/images/manage_boats/managed.svg" alt="" />
                            <span>Managed</span>
                          </div>
                        )}
                      </div>
                    )}
                    {boat.state === 'approved' && <ViewsContainer pageviewCount={boat.pageview_count || 0} />}
                    {(boat.state === 'incomplete' || deniedStates.includes(boat.state)) && !smallScreen && (
                      <button onClick={() => deleteBoat(boat)} className="delete" type="button">
                        Delete listing
                      </button>
                    )}
                  </div>
                  <ButtonWrapper smallScreen={smallScreen}>
                    {needsBuoyUnderwritingAction(boat).length > 0 && (
                      <UpdateInsuranceLink href={`${boatsetterPaths.editListing}/${boat.id}/manage/insurance`}>
                        Update
                      </UpdateInsuranceLink>
                    )}
                    <Button
                      text={boat.state === 'incomplete' ? 'complete listing' : 'edit listing'}
                      onClick={() => openBoat(boat)}
                      outline={boat.state === 'incomplete' || needsBuoyUnderwritingAction(boat).length > 0}
                      variant={
                        boat.state === 'incomplete' || needsBuoyUnderwritingAction(boat).length > 0 ? 'owner' : 'yellow'
                      }
                    />
                    {/* {boat.state === 'denied' && <Button text="delete listing" onClick={() => deleteBoat(boat)} outline variant="owner" />} */}
                    {(boat.state === 'incomplete' || deniedStates.includes(boat.state)) && smallScreen && (
                      <button onClick={() => deleteBoat(boat)} className="delete" type="button">
                        Delete listing
                      </button>
                    )}
                  </ButtonWrapper>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="pagination">
          {!isLoading && (
            <PaginationComponent
              className="owner"
              total={headers?.['x-total']}
              perPage={headers?.['x-per-page']}
              handlePageClick={(destinationPage) => {
                const root = document.getElementById('__next');
                root.scrollTo(0, 0);
                router.push(`/manage_boats?${processQueryOut({ ...query, page: destinationPage })}`);
              }}
              page={query?.page || 1}
            />
          )}
        </div>
      </Wrapper>
      {/* {popUp} */}
      {boatDeleteModal}
    </DefaultLayout>
  );
};

const ButtonWrapper = styled.div<{ smallScreen: boolean }>`
  margin-left: ${(props) => (props.smallScreen ? 0 : 'auto')};
  margin-top: ${(props) => (props.smallScreen ? '16px' : 0)};
  display: flex;
  grid-template-columns: ${(props) => (props.smallScreen ? '1fr' : 'auto auto')};
  grid-column-gap: 16px;
  grid-row-gap: ${(props) => (props.smallScreen ? '16px' : '0')};
`;

const UpdateInsuranceLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  background: #f4c064;
  color: #17233c !important;
  padding: 0px 34px;
  height: 55px;
  font-family: 'AvenirNext';
  font-size: 14px;
  font-weight: bold;
  border-radius: 6px;
  text-align: center;
  &:hover {
    background: #e7a433;
  }
  &:active {
    background: #d28d19;
  }
`;

const Wrapper = styled.div`
  max-width: 1050px;
  margin: 50px auto 100px auto;
  ${mediaMSmMax} {
    max-width: unset;
    margin: 20px 30px 50px 30px;
  }

  .pagination {
    padding-top: 10px;
  }

  ${mediaMdMax} {
    max-width: 650px;
  }

  .arrow {
    display: none;
    ${mediaMSmMax} {
      display: flex;
      cursor: pointer;
      padding-bottom: 20px;
    }

    svg {
      margin: auto 0;
    }

    span {
      color: #0e7873;
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
      margin: auto 0;
      padding-left: 15px;
    }
  }

  .boatsList {
    display: flex;
    flex-flow: column;
    gap: 30px 0;

    .loading {
      display: flex;
      span {
        margin: auto;
      }
    }

    .boat {
      display: flex;
      box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.12);
      border-radius: 10px;
      padding: 30px;
      gap: 0 30px;

      .data {
        display: flex;
        flex-flow: column;
        width: 100%;
        .bottom {
          @media only screen and (max-width: 765px) {
            flex-flow: column;

            &.border-top {
              padding-top: 16px;
              border-top: 1px solid rgba(219, 223, 229, 0.5);
            }
          }

          display: flex;

          .right {
            margin-left: auto;
            margin-top: auto;
            button {
              padding: 0 45px;
            }
            display: flex;
            flex-flow: column;
            gap: 10px 0;
            ${mediaMSmMax} {
              .delete {
                text-align: center;
                padding-top: 10px;
              }

              padding-top: 20px;
              margin-left: 0;
            }
          }

          .left {
            margin-top: auto;
            display: flex;
            ${mediaMMdMin} {
              margin-bottom: 16px;
            }

            .viewsContainer {
              margin-left: auto;
              display: none;
              ${mediaMSmMax} {
                display: flex;
              }

              img {
                padding-right: 5px;
                margin: auto 0;
              }

              span {
                font-weight: 600;
                font-size: 12px;
                line-height: 14px;
                margin: auto 0;
              }

              span span {
                display: none;
              }
            }
          }

          .delete {
            color: #bc350a;
            cursor: pointer;
            font-weight: 500;
            font-size: 16px;
            line-height: 24px;
          }

          margin-top: auto;

          .icons {
            margin-top: auto;
            font-weight: 500;
            font-size: 14px;
            line-height: 14px;
            display: flex;
            gap: 10px;
            flex-flow: column;
            ${mediaMSmMax} {
              flex-flow: row-reverse;

              .managed span {
                display: none;
              }
            }

            .icon {
              display: flex;
              span {
                padding-left: 10px;
                margin: auto 0;
              }
            }
          }
        }

        .heading {
          .container {
            width: 100%;
          }

          display: flex;

          img {
            margin-left: auto;
            border-radius: 5px;
            display: none;
            max-width: 60px;
            width: 100%;
            height: 60px;
            ${mediaMSmMax} {
              display: block;
            }
          }

          .incompleteBoat {
            display: none;
            margin-left: auto;
            background: linear-gradient(0deg, #f5f7f9, #f5f7f9);
            border-radius: 5px;

            width: 60px;
            min-width: 60px;
            height: 60px;

            svg {
              margin: auto;
              transform: scale(0.6);
            }

            ${mediaMSmMax} {
              display: flex;
            }
          }

          ${mediaMSmMax} {
            padding-bottom: 20px;
            &.shortPadding {
              padding-bottom: 10px;
            }
          }

          .title {
            font-weight: 600;
            font-size: 20px;
            ${mediaMSmMax} {
              font-size: 18px;
              padding-right: 30px;
            }
            line-height: 28px;
            a {
              color: #17233c;
            }
          }
        }
      }

      .imageContainer {
        position: relative;
        height: 150px;
        img {
          border-radius: 5px;
          height: 100%;
        }

        .incompleteBoat {
          height: 150px;
          width: 210px;
          //height={140} width={210}
          background: linear-gradient(0deg, #f5f7f9, #f5f7f9);
          border-radius: 5px;
          display: flex;

          svg {
            margin: auto;
          }
        }

        ${mediaMSmMax} {
          display: none;
        }

        .viewsContainer {
          bottom: 15px;
          left: 10px;
          display: flex;
          position: absolute;
          background: #f5f7f9;
          border-radius: 2px;
          padding: 5px 8px;

          img {
            margin: auto;
          }

          span {
            padding-left: 4px;
            margin: auto;
            color: #17233c;
            font-size: 12px;
            line-height: 14px;
            font-weight: 500;
          }
        }
      }
    }
  }

  .header {
    padding-bottom: 50px;
    display: flex;

    ${mediaMSmMax} {
      flex-flow: column;
      padding-bottom: 22px;
    }

    h1 {
      font-weight: bold;
      font-size: 26px;
      line-height: 36px;
      margin: auto 0;
    }

    button {
      margin-left: auto;
      padding: 0 20px;
      ${mediaMSmMax} {
        margin-left: 0;
        border-style: dashed;
        text-align: left;
        margin-top: 25px;

        span::before {
          content: '+';
          border: 1px solid #0e7873;
          color: #0e7873;
          border-radius: 50%;
          margin-right: 10px;
          padding: 2px 7px 3px 7px;
        }
      }
    }
  }
`;

export default MyBoatsPage;
